import { CAN_DOS, CEFR_ORDER, type CanDoDescriptor } from '../data/candos.ts';
import { loadCanDoStates, setCanDoStatus, type CanDoState } from '../data/stores.ts';
import type { CefrSubLevel } from '../data/sample-today.ts';

export function renderAcquis(): HTMLElement {
  const root = document.createElement('section');
  root.className = 'flex flex-col gap-6 max-w-3xl mx-auto';
  root.innerHTML = '<p class="text-[color:var(--color-ink-muted)] mt-12 text-center">Chargement…</p>';
  init(root);
  return root;
}

async function init(root: HTMLElement): Promise<void> {
  const states = await loadCanDoStates();
  root.innerHTML = '';
  root.append(renderHeader(states));

  const grouped = new Map<CefrSubLevel, CanDoDescriptor[]>();
  for (const c of CAN_DOS) {
    if (!grouped.has(c.cefr)) grouped.set(c.cefr, []);
    grouped.get(c.cefr)!.push(c);
  }

  for (const level of CEFR_ORDER) {
    const items = grouped.get(level);
    if (!items || items.length === 0) continue;
    root.append(renderLevelSection(level, items, states));
  }
}

function renderHeader(states: Map<string, CanDoState>): HTMLElement {
  const total = CAN_DOS.length;
  const done = [...states.values()].filter((s) => s.status === 'done').length;
  const inProgress = [...states.values()].filter((s) => s.status === 'in-progress').length;
  const pct = Math.round((done / total) * 100);

  const h = document.createElement('header');
  h.innerHTML = `
    <h1 class="text-3xl font-semibold tracking-tight">Mes acquis</h1>
    <p class="text-sm text-[color:var(--color-ink-muted)] mt-1">Compétences validées selon le Cadre européen commun de référence (CECRL).</p>
    <div class="mt-4 grid grid-cols-3 gap-3">
      <div class="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface-elevated)] p-3 text-center">
        <p class="text-2xl font-semibold text-[color:var(--color-sage)]">${done}</p>
        <p class="text-xs text-[color:var(--color-ink-muted)] uppercase tracking-wider">Acquises</p>
      </div>
      <div class="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface-elevated)] p-3 text-center">
        <p class="text-2xl font-semibold text-[color:var(--color-fjord-600)]">${inProgress}</p>
        <p class="text-xs text-[color:var(--color-ink-muted)] uppercase tracking-wider">En cours</p>
      </div>
      <div class="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface-elevated)] p-3 text-center">
        <p class="text-2xl font-semibold">${pct}%</p>
        <p class="text-xs text-[color:var(--color-ink-muted)] uppercase tracking-wider">du parcours A1→B1</p>
      </div>
    </div>
  `;
  return h;
}

function renderLevelSection(level: CefrSubLevel, items: CanDoDescriptor[], states: Map<string, CanDoState>): HTMLElement {
  const sec = document.createElement('section');
  const doneCount = items.filter((i) => states.get(i.id)?.status === 'done').length;
  sec.innerHTML = `
    <header class="flex items-baseline justify-between mb-2">
      <h2 class="text-lg font-semibold flex items-center gap-2">
        <span class="font-mono px-2 py-0.5 rounded text-xs"
              style="background: var(--color-fjord-50); color: var(--color-fjord-700);">${level}</span>
      </h2>
      <span class="text-xs text-[color:var(--color-ink-muted)]">${doneCount}/${items.length}</span>
    </header>
  `;
  const list = document.createElement('ul');
  list.className = 'divide-y divide-[color:var(--color-border)] rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-elevated)] overflow-hidden';

  for (const item of items) {
    list.append(renderItem(item, states));
  }
  sec.append(list);
  return sec;
}

function renderItem(item: CanDoDescriptor, states: Map<string, CanDoState>): HTMLElement {
  const li = document.createElement('li');
  li.className = 'flex items-center gap-3 px-4 py-3';
  const current = states.get(item.id)?.status ?? 'todo';

  const cycle = document.createElement('button');
  cycle.className = 'w-6 h-6 rounded-full flex items-center justify-center text-xs transition-colors flex-shrink-0';
  applyStatusStyle(cycle, current);
  cycle.setAttribute('aria-label', 'Changer le statut');

  const txt = document.createElement('div');
  txt.className = 'flex-1 flex flex-col';
  txt.innerHTML = `
    <span class="text-sm">${item.text}</span>
    <span class="text-[10px] uppercase tracking-wider text-[color:var(--color-ink-muted)] mt-0.5">${item.domain}</span>
  `;

  const meta = document.createElement('span');
  meta.className = 'text-[10px] font-mono px-2 py-0.5 rounded';
  meta.style.background = 'var(--color-fjord-50)';
  meta.style.color = 'var(--color-fjord-700)';
  meta.textContent = item.cefr;

  cycle.addEventListener('click', async () => {
    const order: CanDoState['status'][] = ['todo', 'in-progress', 'done'];
    const next = order[(order.indexOf(currentStatus(cycle)) + 1) % order.length]!;
    await setCanDoStatus(item.id, next);
    applyStatusStyle(cycle, next);
    if (next === 'done') txt.querySelector('span')!.classList.add('line-through', 'text-[color:var(--color-ink-muted)]');
    else txt.querySelector('span')!.classList.remove('line-through', 'text-[color:var(--color-ink-muted)]');
  });

  if (current === 'done') txt.querySelector('span')!.classList.add('line-through', 'text-[color:var(--color-ink-muted)]');

  li.append(cycle, txt, meta);
  return li;
}

function applyStatusStyle(btn: HTMLElement, status: CanDoState['status']): void {
  btn.dataset.status = status;
  btn.className = 'w-6 h-6 rounded-full flex items-center justify-center text-xs transition-colors flex-shrink-0 ';
  if (status === 'done') {
    btn.className += 'bg-[color:var(--color-sage)] text-white';
    btn.textContent = '✓';
  } else if (status === 'in-progress') {
    btn.className += 'bg-[color:var(--color-stavanger)] text-[color:var(--color-fjord-900)] border-2 border-[color:var(--color-stavanger)]';
    btn.textContent = '◐';
  } else {
    btn.className += 'border-2 border-[color:var(--color-border)] text-transparent';
    btn.textContent = '·';
  }
}

function currentStatus(btn: HTMLElement): CanDoState['status'] {
  return (btn.dataset.status as CanDoState['status']) ?? 'todo';
}
